import {WorkCenterSearchContainer} from '@hconnect/common/components'
import {useEquipment} from '@hconnect/common/hooks/useEquipment'
import {EquipmentIdType, WorkCenter, BaseEvent, EquipmentPlain} from '@hconnect/common/types'
import {getFormPrimaryButtonSx} from '@hconnect/common/utils'
import {Close, FileUpload} from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Box,
  Typography
} from '@mui/material'
import React, {useState, useEffect} from 'react'
import {useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {EquipmentSearchField} from '../../components/form/EquipmentSearchField'
import {FEATURE_NAMESPACE} from '../../consts'
import {useUpdateOilAnalysisAction} from '../../hooks'
import {usePlannerGroups} from '../../hooks/api/usePlannerGroups'
import {CreateOilAnalysisMaintenanceNotificationActionDto, OilAnalysisActionDto} from '../../types'
import {PlannerGroup} from '../../types/plannerGroup.types'

type DialogProps = {
  open: boolean
  event: OilAnalysisActionDto
  workCenters: WorkCenter[]
  plantId: string
  onClose: () => void
  onConfirm: (notification: CreateOilAnalysisMaintenanceNotificationActionDto) => void
}

type FormValues = {
  equipment?: EquipmentPlain
}

export const OilAnalysisCreateMaintenanceNotificationDialogContent: React.FC<DialogProps> = ({
  open,
  event,
  workCenters,
  plantId,
  onClose,
  onConfirm
}) => {
  const {i18n, t} = useTranslation(FEATURE_NAMESPACE)
  const [settings, setSettings] = useState<Partial<BaseEvent> | null>(null)
  const [plannerGroup, setPlannerGroup] = useState<string>('')

  const methods = useForm<FormValues>({
    mode: 'onChange'
  })

  // try to determine default work center
  const mergedEvent = {...event, ...settings}
  const {equipment} = mergedEvent
  const equipmentId = equipment?.id
  const equipmentIdType = equipment?.idType
  const {data: plannerGroups} = usePlannerGroups({plantId: plantId})
  const equipmentPlain = useEquipment(
    plantId,
    equipmentId,
    equipmentIdType as EquipmentIdType,
    i18n.language,
    {
      enabled: Boolean(equipmentId && equipmentIdType)
    }
  )

  const [selectedWorkCenter, setSelectedWorkCenter] = useState<WorkCenter | undefined>()

  const updateAction = useUpdateOilAnalysisAction()

  useEffect(() => {
    const defaultWorkCenter = workCenters.find(
      (wc) => wc.id === equipmentPlain.data?.defaultWorkCenterId
    )
    setSelectedWorkCenter(defaultWorkCenter)
  }, [equipmentPlain.data?.defaultWorkCenterId, workCenters])

  useEffect(() => {
    const plannerGroupId = equipmentPlain.data?.defaultPlannerGroup
    setPlannerGroup(plannerGroupId ?? '')
  }, [equipmentPlain.data?.defaultPlannerGroup])

  const onExit = () => {
    setSettings(null)
  }

  const onEquipmentChange = (equipment: EquipmentPlain | undefined) => {
    setSettings(!equipment ? null : {equipment: equipment.equipment})
  }

  const onSend = () => {
    if (!settings) {
      onConfirm({
        workCenterId: selectedWorkCenter?.id,
        plannerGroupId: plannerGroup
      })
      return
    }
    const selectedEquipment = settings.equipment
    if (selectedEquipment) {
      updateAction.mutate(
        {
          id: event.id,
          plantUpmId: event.plantUpmId,
          title: event.title,
          description: event.description,
          status: event.status,
          dueDate: event.dueDate ?? null,
          functionalLocation: selectedEquipment.text,
          processStage: event.processStage,
          assignees: event.assignees.map((a) => a.id),
          equipmentId: selectedEquipment.id,
          equipmentIdType: selectedEquipment.idType,
          equipmentText: selectedEquipment.text
        },
        {
          onSuccess: () => {
            onConfirm({
              workCenterId: selectedWorkCenter?.id,
              plannerGroupId: plannerGroup
            })
          }
        }
      )
    }
  }

  const renderPlannerGroup = ({plannerGroupId, plannerGroupName}: PlannerGroup) => {
    const optionLabel = `${plannerGroupId} - ${plannerGroupName}`
    return (
      <MenuItem key={plannerGroupId} value={plannerGroupId}>
        {optionLabel}
      </MenuItem>
    )
  }

  return (
    <Dialog
      TransitionProps={{
        onExit
      }}
      open={open}
      maxWidth="sm"
      data-test-id="oil-analysis-actions-maintenance-notification-dialog"
      onClose={onClose}
    >
      <DialogTitle>
        {t('oilAnalysisActions.sendMaintenanceNotificationDialog.workCenterQuestion')}
      </DialogTitle>
      <DialogContent>
        {!event.equipment?.id && (
          <Box pb={2}>
            <Typography gutterBottom>
              {t('oilAnalysisActions.sendMaintenanceNotificationDialog.selectEquipment')}
            </Typography>
            <EquipmentSearchField
              plantId={plantId}
              name="equipment"
              label={t('oilAnalysisActions.sendMaintenanceNotificationDialog.functionalLocation')}
              control={methods.control}
              onChange={onEquipmentChange}
            />
          </Box>
        )}
        <Typography gutterBottom>
          {t('oilAnalysisActions.sendMaintenanceNotificationDialog.workCenterOptionalQuestion')}
        </Typography>
        <WorkCenterSearchContainer
          required={false}
          t={t}
          workCenter={selectedWorkCenter}
          handleWorkCenterSelection={(workCenter) => setSelectedWorkCenter(workCenter)}
          workCenters={workCenters}
        />
        <Box pt={2}>
          <TextField
            data-test-id={'oil-analysis-actions-maintenance-notification-dialog-planner-group'}
            select
            value={plannerGroup}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setPlannerGroup(event.target.value ?? '')
            }}
            fullWidth
            label={t('oilAnalysisActions.sendMaintenanceNotificationDialog.plannerGroup')}
          >
            <MenuItem value={''}>&nbsp;</MenuItem>
            {plannerGroups?.map(renderPlannerGroup)}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<Close />}
          variant="text"
          onClick={onClose}
          data-test-id="oil-analysis-actions-maintenance-notification-dialog-cancel"
        >
          {t('oilAnalysisActions.sendMaintenanceNotificationDialog.action.cancel')}
        </Button>
        <Button
          onClick={() => onSend()}
          startIcon={<FileUpload />}
          color="primary"
          variant="text"
          disabled={!mergedEvent.equipment}
          sx={getFormPrimaryButtonSx}
          data-test-id="oil-analysis-actions-maintenance-notification-dialog-create-notification"
        >
          {t(
            'oilAnalysisActions.sendMaintenanceNotificationDialog.action.createMaintenanceNotification'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
